import * as Sentry from '@sentry/react';

interface FallbackProps {
  error: Error;
}

function ErrorFallback({ error }: FallbackProps) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-black p-4 text-white">
      <div className="max-w-md text-center">
        <div className="mb-6 text-6xl">🦝💥</div>

        <h1 className="mb-4 font-hyperspace text-2xl font-bold text-amber-400">
          Oops! Something went wrong
        </h1>

        <p className="mb-6 text-gray-400">
          Our raccoons are working hard to fix this issue. Please try refreshing the page.
        </p>

        {import.meta.env.DEV && (
          <details className="mb-6 text-left">
            <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-400">
              Technical Details
            </summary>
            <pre className="mt-2 max-h-40 overflow-auto rounded bg-gray-900 p-3 text-xs text-red-400">
              {error.message}
            </pre>
          </details>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <button
            onClick={() => Sentry.showReportDialog()}
            className="rounded-lg bg-amber-500 px-6 py-3 font-semibold text-black transition-colors hover:bg-amber-400"
          >
            Report Issue
          </button>
          <button
            onClick={() => window.location.reload()}
            className="rounded-lg border border-gray-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-gray-800"
          >
            Refresh Page
          </button>
        </div>
      </div>
    </div>
  );
}

export function SentryErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <Sentry.ErrorBoundary
      fallback={(props) => <ErrorFallback {...(props as FallbackProps)} />}
      showDialog={false}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

export default SentryErrorBoundary;
